// @ts-strict-ignore
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { WagoApiClient } from '@shared/service/wago-api.client';
import { ModelConversionService } from '@shared/convert/model-conversion.service';
import { NumberUtils } from '@shared/util/number-utils';
import { StringUtils } from '@shared/util/string-utils';
import { ProjectGroupVO } from '../../../model/vo/project-group.vo';
import { HttpParams } from '@angular/common/http';
import { CopyProjectOperationProperties } from '@project/components/project/copy/copy.component';
import { Injectable } from '@angular/core';

interface SearchBaseDTO {
  searchString: string;
  projectsLimit: number;
  includeOptionValues: boolean;
}

@Injectable({ providedIn: 'root' })
export class ArticleProjectGroupDataService {
  private readonly loadArticleProjectGroupByIdUrl: string = 'article-project-group/';
  private readonly loadBaseArticleProjectGroupsUrl: string = 'article-project-group/base/';
  private readonly searchArticleProjectGroupUrl: string = 'article-project-group/search';
  private readonly commentArticleProjectGroupUrl: string = 'article-project-group/comment';

  constructor(private modelConversionService: ModelConversionService, private wagoApiClient: WagoApiClient) { }

  public loadBaseArticleProjectGroups(level: number = 0): Observable<ProjectGroupVO[]> {
    return this.wagoApiClient.get(`${this.loadBaseArticleProjectGroupsUrl}?level=${level}`)
      .pipe(map((response: any) => this.modelConversionService.convertProjectGroupsVO(response)));
  }

  public loadArticleProjectGroupById(groupId: number, level: number = 0): Observable<ProjectGroupVO> {
    if (!NumberUtils.isNumber(groupId) || !Number.isInteger(groupId)) {
      return of(null);
    }

    return this.wagoApiClient.get(`${this.loadArticleProjectGroupByIdUrl}${groupId}?level=${level}`)
      .pipe(map((response: any) =>
        this.modelConversionService.convertProjectGroupVO(response)));
  }

  public searchArticleProjectGroups(searchString: string, includeOptionValues: boolean, projectsLimit: number = 500): Observable<ProjectGroupVO[]> {
    if (StringUtils.isEmpty(searchString)) {
      return of([]);
    }

    const requestBody: SearchBaseDTO = {searchString, projectsLimit, includeOptionValues};

    return this.wagoApiClient.post(this.searchArticleProjectGroupUrl, requestBody)
      .pipe(map((response: any) => this.modelConversionService.convertProjectGroupsVO(response)));
  }

  public saveArticleProjectGroup(projectGroupVO: ProjectGroupVO): Observable<ProjectGroupVO> {
    return this.wagoApiClient.post(this.loadArticleProjectGroupByIdUrl, projectGroupVO, null,
      { isErrorHandlingRequired: false })
      .pipe(map((response: any) => this.modelConversionService.convertProjectGroupVO(response)));
  }
  public moveArticleProjectGroup(groupId: number, targetGroupId: number): Observable<ProjectGroupVO> {
    if (!NumberUtils.isNumber(groupId) || !Number.isInteger(groupId)) {
      return of(null);
    }
    let httpParams: HttpParams = new HttpParams();
    if (targetGroupId) {
      httpParams = httpParams.append('targetGroupId', targetGroupId.toString());
    }
    const requestOptions = {
      params: httpParams
    };
    return this.wagoApiClient.put(`${this.loadArticleProjectGroupByIdUrl}${groupId}/move`, null, requestOptions,
      { isErrorHandlingRequired: false }).pipe(
        map((response) => this.modelConversionService.convertProjectGroupVO(response)));
  }

  public copyArticleProjectGroup(groupId: number, targetGroupId: number, copyProperties: CopyProjectOperationProperties, newName: string = null): Observable<ProjectGroupVO> {
    if (!NumberUtils.isNumber(groupId) || !Number.isInteger(groupId)) {
      return of(null);
    }
    let httpParams: HttpParams = new HttpParams();
    if (targetGroupId) {
      httpParams = httpParams.append('targetGroupId', targetGroupId.toString());
    }
    httpParams = httpParams.append('copyProjectProperties', copyProperties.copyConfigurationProperties.toString());
    httpParams = httpParams.append('copyGroupProperties', copyProperties.copyProjectProperties.toString());
    httpParams = httpParams.append('newName', newName);
    const requestOptions = {
      params: httpParams
    };
    return this.wagoApiClient.put(`${this.loadArticleProjectGroupByIdUrl}${groupId}/copy`, null, requestOptions,
      { isErrorHandlingRequired: false }).pipe(
      map((response) => this.modelConversionService.convertProjectGroupVO(response)));
  }

  public commentProjectGroup(isArticleProjectGroup: boolean, projectGroup: ProjectGroupVO): void {
    if (!isArticleProjectGroup || !projectGroup) {
      return;
    }
    const dto = {
      groupId: projectGroup.id,
      comment: projectGroup.comment,
      internalComment: projectGroup.internalComment
    };
    this.wagoApiClient.post(this.commentArticleProjectGroupUrl, dto).subscribe();
  }
}
